import { useMemo, useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { getRepo, ValidationError, type Article, type Category } from '../../lib/repo';
import { useAsync } from '../../lib/hooks';
import { slugify, isValidSlug } from '../../shared/slug.js';
import { validate, categorySchema } from '../../shared/schema.js';
import {
  Badge, Button, Card, CardHeader, Dialog, EmptyState, Field, Input, Spinner, Textarea, useToast,
} from '../../components/ui';

interface Draft {
  id: string | null;
  name: string;
  slug: string;
  description: string;
  sortOrder: number;
  slugTouched: boolean;
}

const emptyDraft = (nextOrder: number): Draft => ({
  id: null, name: '', slug: '', description: '', sortOrder: nextOrder, slugTouched: false,
});

export function CategoriesPage() {
  const toast = useToast();

  const { data: categories, loading, error, reload } = useAsync<Category[]>(() => getRepo().listCategories(), []);
  const { data: articles } = useAsync<Article[]>(() => getRepo().listAllArticles(), []);

  // Server state is the source of truth: every write goes through the repository
  // and the list is re-read afterwards, so the UI can never drift from what was stored.
  const cats = categories ?? [];

  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState('');
  const [removing, setRemoving] = useState(false);

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of articles ?? []) map.set(a.categoryId, (map.get(a.categoryId) ?? 0) + 1);
    return map;
  }, [articles]);

  const previewSlug = draft
    ? draft.slugTouched
      ? draft.slug
      : draft.name.trim()
        ? slugify(draft.name, { fallback: 'category' })
        : ''
    : '';

  function openNew() {
    setErrors({});
    setDraft(emptyDraft(cats.length + 1));
  }

  function openEdit(c: Category) {
    setErrors({});
    setDraft({ id: c.id, name: c.name, slug: c.slug, description: c.description, sortOrder: c.sortOrder, slugTouched: true });
  }

  function setName(name: string) {
    setDraft((d) => (d ? { ...d, name, slug: d.slugTouched ? d.slug : name.trim() ? slugify(name, { fallback: 'category' }) : '' } : d));
  }

  async function save() {
    if (!draft) return;
    const value = {
      name: draft.name,
      slug: previewSlug,
      description: draft.description,
      sortOrder: draft.sortOrder,
    };
    const res = validate(categorySchema, value);
    if (!res.ok) { setErrors(res.errors); return; }

    setSaving(true);
    setErrors({});
    try {
      await getRepo().saveCategory(
        {
          ...(draft.id ? { id: draft.id } : {}),
          name: value.name,
          slug: previewSlug,
          description: value.description,
          sortOrder: value.sortOrder,
        },
      );
      toast(draft.id ? 'Category updated' : 'Category added');
      setDraft(null);
      reload();
    } catch (e) {
      if (e instanceof ValidationError) {
        setErrors(e.errors);
      } else {
        setErrors({ _: e instanceof Error ? e.message : 'Could not save the category.' });
      }
      toast('Could not save the category', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    setRemoving(true);
    setRemoveError('');
    try {
      await getRepo().deleteCategory(id);
      toast('Category removed');
      setConfirmId(null);
      reload();
    } catch (e) {
      setRemoveError(e instanceof Error ? e.message : 'Could not remove the category.');
    } finally {
      setRemoving(false);
    }
  }

  if (loading && !cats.length) return <Spinner label="Loading categories…" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Categories</h1>
          <p className="mt-1 text-sm text-muted">{cats.length} categories · shown in site navigation in sort order.</p>
        </div>
        <Button onClick={openNew}><Plus className="h-4 w-4" /> Add category</Button>
      </div>

      {error ? <p className="text-sm text-danger">{error}</p> : null}

      {cats.length === 0 ? (
        <EmptyState title="No categories yet" description="Create the first section to start filing articles." action={<Button onClick={openNew}>Add category</Button>} />
      ) : (
        <Card>
          <CardHeader title="All categories" description="Slugs are URL-safe by construction — only lowercase letters, numbers and single hyphens." />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted">
                  <th className="px-5 py-3">#</th>
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Slug</th>
                  <th className="px-5 py-3">Articles</th>
                  <th className="px-5 py-3">Description</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {cats.map((c, i) => (
                  <tr key={c.id} className="border-b border-border last:border-0">
                    <td className="px-5 py-3 text-muted">{c.sortOrder || i + 1}</td>
                    <td className="px-5 py-3 font-medium">{c.name}</td>
                    <td className="px-5 py-3">
                      <code className="rounded bg-background px-1.5 py-0.5 text-xs text-muted">/category/{c.slug}</code>
                    </td>
                    <td className="px-5 py-3"><Badge tone="published">{counts.get(c.id) ?? 0}</Badge></td>
                    <td className="max-w-xs truncate px-5 py-3 text-muted">{c.description || '—'}</td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" aria-label={`Edit ${c.name}`} onClick={() => openEdit(c)}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Delete ${c.name}`}
                          onClick={() => { setRemoveError(''); setConfirmId(c.id); }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={draft !== null} onClose={() => setDraft(null)} title={draft?.id ? 'Edit category' : 'Add category'}>
        {draft ? (
          <div className="space-y-4">
            {errors._ ? <p className="text-sm text-danger">{errors._}</p> : null}
            <Field label="Name" error={errors.name}>
              <Input value={draft.name} onChange={(e) => setName(e.target.value)} placeholder="Literature" />
            </Field>
            <Field label="Slug" error={errors.slug} hint={`Used in URLs: /category/${previewSlug || '…'}`}>
              <Input
                value={previewSlug}
                onChange={(e) => setDraft({ ...draft, slug: e.target.value, slugTouched: true })}
                placeholder="literature"
              />
            </Field>
            {previewSlug && !isValidSlug(previewSlug) ? (
              <p className="text-xs text-danger">Slug must contain only lowercase letters, numbers and single hyphens.</p>
            ) : null}
            {previewSlug && slugify(previewSlug, { fallback: 'category' }) !== previewSlug ? (
              <p className="text-xs text-muted">This slug will be normalised to <code>{slugify(previewSlug, { fallback: 'category' })}</code> on save.</p>
            ) : null}
            <Field label="Description" error={errors.description} hint={`${draft.description.length}/200 characters`}>
              <Textarea rows={3} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} maxLength={200} />
            </Field>
            <Field label="Sort order" error={errors.sortOrder}>
              <Input
                type="number" min={0} max={999} value={draft.sortOrder}
                onChange={(e) => setDraft({ ...draft, sortOrder: Number(e.target.value) })}
                className="w-32"
              />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDraft(null)} disabled={saving}>Cancel</Button>
              <Button onClick={() => void save()} disabled={saving}>
                {saving ? 'Saving…' : draft.id ? 'Save changes' : 'Add category'}
              </Button>
            </div>
          </div>
        ) : null}
      </Dialog>

      <Dialog open={confirmId !== null} onClose={() => setConfirmId(null)} title="Delete category">
        <p className="text-sm text-muted">
          Removing a category will not delete its articles, but they will need to be re-filed. This cannot be undone.
        </p>
        {removeError ? <p className="mt-3 text-sm text-danger">{removeError}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setConfirmId(null)} disabled={removing}>Cancel</Button>
          <Button variant="danger" onClick={() => confirmId && void remove(confirmId)} disabled={removing}>
            {removing ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
